import {
  createPuppyAssemblyProjectModelCoverageContext,
  CompleteGlbOccurrenceGraphAnalyzer,
  createMaterializedPuppyRootSourceChildBridges,
  createInternalPuppyAssemblyExpandTransactionState,
  createProjectModelLoadPlan,
  executeProjectModelLoad,
  isAssemblyPersistedDocument,
  materializePuppyProjectModelTree,
  mergeMaterializedPuppyRootChildrenByProvenance,
  migrateLegacyPuppyProjectModelDocument,
  normalizeProjectModelAssemblyDocument,
  parsePuppyPackageProjectModelDocuments,
  parseGlbJsonDocument,
  InstanceIdCompleteGlbRootNodeSelectionStrategy,
  resolvePuppyAssemblyPackageRootFile,
  resolveAssemblyDocumentRootNodeKey,
  type AssemblyDocument,
  type ModelAssetFile,
  type ModelAssetNode,
  type ModelAssetReader,
  type ProjectModelLoadPlan,
  type ProjectModelLoadResult,
  type ProjectModelRootLoadPlanInput,
  type ProjectModelSceneBinding,
  type PuppyAssemblyProjectModelCoverageContext,
  type PuppyAssemblyDocument,
  type PuppyProjectModelDocument,
  type InternalPuppyAssemblyExpandTransactionState,
} from '@puppycad/cad-core'
import { CompleteGlbPackageReader } from './complete-glb-package'
import {
  buildProjectNodeFileReadKey,
  buildProjectAssetFileIdentityKey,
  collectMissingProjectModelFileReadTasksFromCache,
  collectProjectNodeFileReadTasks,
  completeProjectModelFileReadResultsFromEmbeddedContainers,
  createPreloadedProjectModelReader,
  readProjectModelFileReadTasks,
  type ProjectNodeFileReadResult,
} from './project-model-file-read-utils'
import {
  createRootPuppyAssemblyMissingFirstLevelNodeSnapshotForInternalExpandTransaction,
  type RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult,
} from './project-model-loading-service'
import type {
  NodeReloadApiAdapter,
  ProjectModelFileByteCacheAdapter,
} from './types/project-model-loading-types'

/**
 * 装配 direct child 后端扩展的最大轮数。
 *
 * 设计说明：一个 loading unit 允许多轮 GetNodeTree 补齐数据树；上限只用于阻断坏数据导致的
 * “补入后仍被判定缺失”循环，不代表每一轮都可以触发 unit 解析。
 */
const MAX_PUPPY_ASSEMBLY_DIRECT_CHILD_PATCH_PASSES = 32

/**
 * 可从外层只读缓存读取到字节的项目模型文件集合。
 */
interface ReadableCachedProjectModelFiles {
  /**
   * 可读缓存文件的身份 key 集合。
   */
  readonly identityKeys: ReadonlySet<string>
  /**
   * 由可读缓存字节补入本次 staged 区的文件读取结果。
   */
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
}

/**
 * 单轮装配 direct child 后端扩展结果。
 */
interface PuppyAssemblyDirectChildPatchPassResult {
  /**
   * 本轮扩展后的完整 load root。
   */
  readonly root: ModelAssetNode
  /**
   * 本轮新增的 direct child 节点 key。
   */
  readonly insertedNodeKeys: readonly string[]
  /**
   * 本轮发现但没有采纳版本的 direct child 节点 key。
   */
  readonly skippedUnversionedNodeKeys: readonly string[]
  /**
   * 本轮发现但版本字段损坏的 direct child 节点 key。
   */
  readonly invalidNodeKeys: readonly string[]
  /** 同次 expand 内部事务状态；不会进入公共加载结果。 */
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
}

/** 一次 loading unit expand 内共享的私有解析与 RPC 状态。 */
interface InternalProjectModelBackendExpandContext {
  /** 相同节点与 package 文件的源文档解析 Promise，包含迁移后的 null 或空项目模型。 */
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
  /** direct child RPC 在多 pass 间使用的事务去重状态。 */
  readonly puppyAssemblyTransactionState: InternalPuppyAssemblyExpandTransactionState
  /** complete 归档按 nodeKey/package 身份复用的准备快照 Promise。 */
  readonly completeGlbPackageSnapshotPromiseByReadKey: Map<
    string,
    Promise<PuppyCADWeb.PreparedCompleteGlbPackageSnapshot>
  >
}

/** 一次 expand 内缓存的 `.puppy` 装配与最终项目模型文档。 */
interface InternalPuppyProjectModelDocuments {
  /** 单次解压解析得到的装配语义文档。 */
  readonly assembly: PuppyAssemblyDocument
  /** 原生或 legacy migration 后的项目模型；无法迁移时为 null。 */
  readonly projectModel: PuppyProjectModelDocument | null
}

/**
 * loading unit 后端数据树扩展结果。
 */
interface ExpandedProjectModelBackendLoadUnit {
  /**
   * 数据扩展后的 unit 根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 数据扩展后最终执行 cad-core load 使用的计划。
   */
  readonly plan: ProjectModelLoadPlan
  /**
   * 数据扩展后完整 staged 文件读取结果。
   */
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  /**
   * 数据扩展阶段累计的 direct child 诊断。
   */
  readonly rootPuppyPatch: RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult
  /** 当前最终数据树中每个 complete loading node 的准备快照。 */
  readonly completeGlbPackageSnapshotsByNodeKey: ReadonlyMap<
    string,
    PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
  >
}

/**
 * 统一后端项目模型加载准备器输入。
 *
 * 设计说明：准备器只负责“单个加载单元”的读取准备与 cad-core 执行编排；
 * CadNode 到 ModelAssetNode 的映射、插入零部件结构生成和外层字节缓存写入仍由调用方负责。
 * 准备器可以读取外层只读缓存中的可用字节来构造本次 preloaded reader，但不会写入缓存。
 */
export interface PrepareProjectModelBackendLoadInput {
  /**
   * cad-core 项目模型加载单元计划输入。
   *
   * 设计说明：统一准备器只处理已经由业务层切出的 root-load unit；加载后的 OpenProject/节点替换等
   * 业务状态合并仍留在调用方，避免在数据读取阶段混入重组语义。
   */
  readonly planInput: ProjectModelRootLoadPlanInput
  /**
   * 授权后的版本文件读取器。
   */
  readonly reader: ModelAssetReader
  /**
   * 后端服务根地址，用于策略允许的后端树扩展场景重建下载 URL。
   */
  readonly backendBaseUrl: string
  /**
   * 外层只读文件字节缓存适配器；准备器会读取可用缓存字节，但不写入缓存。
   */
  readonly fileCache: ProjectModelFileByteCacheAdapter
  /**
   * 可选节点重载适配器；存在时才具备后端树扩展能力。
   */
  readonly nodeReloadAdapter?: NodeReloadApiAdapter
  /**
   * 当前场景对象绑定；后端树扩展后二次执行时会沿用该绑定集合。
   */
  readonly sceneBindings?: readonly ProjectModelSceneBinding[]
  /**
   * 外层传入的装配文档覆盖表。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /**
   * 可选取消信号，用于中断版本文件读取。
   */
  readonly signal?: AbortSignal
}

/**
 * 统一后端项目模型加载准备器结果。
 */
export interface PreparedProjectModelBackendLoadResult {
  /**
   * 最终 cad-core 项目模型加载结果。
   */
  readonly loadResult: ProjectModelLoadResult
  /**
   * 最终执行使用的加载计划；发生后端树扩展后会是重建后的 root-load 计划。
   */
  readonly plan: ProjectModelLoadPlan
  /**
   * 本次准备器实际预读并 staged 的文件字节。
   */
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  /**
   * 后端树扩展诊断结果；未启用或没有缺失节点时返回空 patch 结构。
   */
  readonly rootPuppyPatch: RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult
  /** 供正式 GLTFLoader 复用的 complete 归档准备快照。 */
  readonly completeGlbPackageSnapshotsByNodeKey: ReadonlyMap<
    string,
    PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
  >
}

/**
 * 创建后端树扩展的空结果。
 *
 * @param root 当前加载单元 root。
 * @returns 符合 RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult 的空 patch。
 */
function createEmptyRootPuppyPatch(
  root: ModelAssetNode,
): RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult {
  return {
    root,
    insertedNodeKeys: [],
    skippedUnversionedNodeKeys: [],
    invalidNodeKeys: [],
  }
}

/**
 * 按前序收集当前模型资产树中的节点。
 *
 * @param node 当前递归入口节点。
 * @returns 从当前节点开始的前序节点列表。
 */
function collectModelAssetNodesPreorder(node: ModelAssetNode): readonly ModelAssetNode[] {
  return [
    node,
    ...node.children.flatMap((child) => collectModelAssetNodesPreorder(child)),
  ]
}

/**
 * 在当前 load root 中按 nodeKey 查找节点。
 *
 * @param root 当前加载单元根节点。
 * @param nodeKey 目标节点 key。
 * @returns 命中的模型节点；未找到时返回 null。
 */
function findModelAssetNodeByKey(root: ModelAssetNode, nodeKey: string): ModelAssetNode | null {
  if (root.nodeKey === nodeKey) {
    return root
  }

  for (const child of root.children) {
    const found = findModelAssetNodeByKey(child, nodeKey)

    if (found) {
      return found
    }
  }

  return null
}

/**
 * 不可变地替换当前 load root 中的一个子树。
 *
 * @param root 当前递归入口。
 * @param targetNodeKey 需要替换的节点 key。
 * @param nextNode 替换后的节点。
 * @returns 替换后的递归入口。
 */
function replaceModelAssetNodeByKey(
  root: ModelAssetNode,
  targetNodeKey: string,
  nextNode: ModelAssetNode,
): ModelAssetNode {
  if (root.nodeKey === targetNodeKey) {
    return nextNode
  }

  let changed = false
  const children = root.children.map((child) => {
    const nextChild = replaceModelAssetNodeByKey(child, targetNodeKey, nextNode)

    if (nextChild !== child) {
      changed = true
    }

    return nextChild
  })

  return changed ? { ...root, children } : root
}

/**
 * 向诊断数组追加去重后的节点 key。
 *
 * @param target 需要追加的目标数组。
 * @param values 本次产生的节点 key。
 */
function appendUniqueNodeKeys(target: string[], values: readonly string[]): void {
  for (const value of values) {
    if (!target.includes(value)) {
      target.push(value)
    }
  }
}

/**
 * 从 staged 结果中查找指定节点的逻辑文件读取结果。
 *
 * 设计说明：同一物理文件可能被多个 nodeKey 消费，direct child 扩展必须使用当前节点自己的
 * `nodeKey + role + file` 逻辑记录，避免把外层 unit root 的 `_assembly.json` 误当成子装配文档。
 *
 * @param input 节点 key、文件职责和 staged 文件结果。
 * @returns 命中的逻辑读取结果；缺失时返回 null。
 */
function findProjectNodeFileReadResult(input: {
  readonly nodeKey: string
  readonly file: ModelAssetNode['assemblyManifest'] | ModelAssetNode['packageFiles'][number]
  readonly role: ProjectNodeFileReadResult['role']
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
}): ProjectNodeFileReadResult | null {
  if (!input.file) {
    return null
  }

  const readKey = buildProjectNodeFileReadKey(input.nodeKey, input.file, input.role)

  return input.fileReadResults.find((result) => (
    buildProjectNodeFileReadKey(result.nodeKey, result.file, result.role) === readKey
  )) ?? null
}

/**
 * 读取 complete GLB 文件加载描述声明的业务根选择身份。
 *
 * 设计说明：`ModelAssetNode.instanceUuid` 表示父装配中的稳定 occurrence；版本切换时它会继承
 * 旧槽位，因此不能用于查询目标版本包。根选择必须只消费 mapper 从当前 `CadNode.instanceId`
 * 写入文件加载描述的版本内身份。
 *
 * @param node 当前 complete loading node。
 * @param packageFile 当前节点的 complete GLB 文件加载描述。
 * @returns 当前版本包内用于唯一选择业务根的 `instance_id`。
 * @throws 当 complete GLB 文件加载描述缺少根选择身份时。
 */
function resolveCompleteGlbRootInstanceId(
  node: ModelAssetNode,
  packageFile: ModelAssetFile,
): string {
  const instanceId = packageFile.completeGlbRootInstanceId?.trim() ?? ''

  if (!instanceId) {
    throw new Error(
      `complete GLB loading file 缺少根选择 instance_id：nodeKey=${node.nodeKey}, fileId=${packageFile.fileId}`,
    )
  }

  return instanceId
}

/**
 * 从 staged 归档字节构造 complete GLB 的解析前快照。
 *
 * 同一 nodeKey/package read key 的 Promise 进入 loading unit 私有缓存，因此多轮 GetNodeTree
 * 扩展只复用第一次解压、JSON 读取和 occurrence 分析结果，不跨版本或跨 loading unit 共享。
 *
 * @param input 当前节点、staged 字节、取消信号和 unit 私有快照缓存。
 * @returns 当前节点不是 complete 模式时返回 null；否则返回稳定 prepared snapshot。
 */
function resolvePreparedCompleteGlbPackageSnapshot(input: {
  readonly node: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly signal?: AbortSignal
  readonly snapshotPromiseByReadKey: Map<
    string,
    Promise<PuppyCADWeb.PreparedCompleteGlbPackageSnapshot>
  >
}): Promise<PuppyCADWeb.PreparedCompleteGlbPackageSnapshot> | null {
  const packageFile = input.node.packageFiles.find(file => file.loadMode === 'complete-glb-scene')
  if (!packageFile) {
    return null
  }

  resolveCompleteGlbRootInstanceId(input.node, packageFile)
  const readResult = findProjectNodeFileReadResult({
    nodeKey: input.node.nodeKey,
    file: packageFile,
    role: 'node-package',
    fileReadResults: input.fileReadResults,
  })
  if (!readResult) {
    throw new Error(
      `complete GLB staged bytes missing：nodeKey=${input.node.nodeKey}, fileId=${packageFile.fileId}`,
    )
  }

  const readKey = buildProjectNodeFileReadKey(input.node.nodeKey, packageFile, 'node-package')
  const cached = input.snapshotPromiseByReadKey.get(readKey)
  if (cached) {
    return cached
  }

  const snapshotPromise = prepareCompleteGlbPackageSnapshot({
    node: input.node,
    packageFile,
    bytes: readResult.bytes,
    assertCurrent: () => input.signal?.throwIfAborted(),
  })
  input.snapshotPromiseByReadKey.set(readKey, snapshotPromise)
  return snapshotPromise
}

/**
 * 从单个 complete loading node 的归档字节构造正式加载快照。
 *
 * 该入口既供后端加载准备器复用，也供删除恢复等不经过后端请求的本地 loading unit 使用；
 * 调用方必须在本次正式 GLTF 实例化完成前持有返回值，禁止跨 loading unit 缓存 Object3D。
 *
 * @param input 项目节点、归档文件、已读取字节与请求有效性断言。
 * @returns 同一次解包产生的文件系统、原始 JSON、根选择和 occurrence 图。
 */
export async function prepareCompleteGlbPackageSnapshot(input: {
  readonly node: ModelAssetNode
  readonly packageFile: ModelAssetFile
  readonly bytes: Uint8Array
  readonly assertCurrent?: () => void
}): Promise<PuppyCADWeb.PreparedCompleteGlbPackageSnapshot> {
  const instanceId = resolveCompleteGlbRootInstanceId(input.node, input.packageFile)

  const completePackage = await new CompleteGlbPackageReader().read({
    packageFile: input.packageFile,
    bytes: input.bytes,
    assertCurrent: input.assertCurrent,
  })
  input.assertCurrent?.()
  const sourceGlbBytes = await completePackage.readFileBytes(completePackage.sourceGlbFile)
  const rawJson = parseGlbJsonDocument(sourceGlbBytes)
  const rootSelection = new InstanceIdCompleteGlbRootNodeSelectionStrategy(instanceId).select(rawJson)
  const occurrenceGraph = new CompleteGlbOccurrenceGraphAnalyzer().analyze({
    json: rawJson,
    selection: rootSelection,
  })
  input.assertCurrent?.()

  return {
    packageFile: input.packageFile,
    sourceGlbFile: completePackage.sourceGlbFile,
    projectFiles: completePackage.projectFiles,
    readFileBytes: completePackage.readFileBytes,
    rawJson,
    rootSelection,
    occurrenceGraph,
  }
}

/**
 * 为当前最终数据树收集所有 complete loading node 的准备快照。
 *
 * @param input 当前 root、staged 字节与 loading unit 私有缓存。
 * @returns nodeKey 到 prepared snapshot 的只读索引。
 */
async function createPreparedCompleteGlbPackageSnapshotsByNodeKey(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly signal?: AbortSignal
  readonly snapshotPromiseByReadKey: Map<
    string,
    Promise<PuppyCADWeb.PreparedCompleteGlbPackageSnapshot>
  >
}): Promise<ReadonlyMap<string, PuppyCADWeb.PreparedCompleteGlbPackageSnapshot>> {
  const snapshots = new Map<string, PuppyCADWeb.PreparedCompleteGlbPackageSnapshot>()

  for (const node of collectModelAssetNodesPreorder(input.root)) {
    const snapshotPromise = resolvePreparedCompleteGlbPackageSnapshot({
      node,
      fileReadResults: input.fileReadResults,
      signal: input.signal,
      snapshotPromiseByReadKey: input.snapshotPromiseByReadKey,
    })
    if (snapshotPromise) {
      snapshots.set(node.nodeKey, await snapshotPromise)
    }
  }
  return snapshots
}

/**
 * 解析 staged `_assembly.json` 字节为装配文档。
 *
 * 设计说明：这里复用 cad-core 的归一化规则，只把解析时机提前到数据扩展阶段；最终加载结果
 * 仍由 `executeProjectModelLoad` 再次通过相同规则生成，避免 preparer 变成业务状态写入方。
 *
 * @param node 当前 `_assembly.json` 所属的模型节点。
 * @param bytes staged 文件字节。
 * @returns 可用于 direct child 差异分析的装配文档。
 */
function parseStagedAssemblyDocumentBytes(node: ModelAssetNode, bytes: Uint8Array): AssemblyDocument {
  let raw: unknown

  try {
    raw = JSON.parse(new TextDecoder().decode(bytes))
  } catch (error) {
    throw new Error(`assembly document parse failed: nodeKey=${node.nodeKey}`, { cause: error })
  }

  let document: AssemblyDocument

  try {
    document = normalizeProjectModelAssemblyDocument(node, raw)
  } catch (error) {
    throw new Error(`assembly document validation failed: nodeKey=${node.nodeKey}`, { cause: error })
  }

  if (!isAssemblyPersistedDocument(document) && resolveAssemblyDocumentRootNodeKey(document) !== node.nodeKey) {
    throw new Error(`assembly document root mismatch: nodeKey=${node.nodeKey}`)
  }

  return document
}

/**
 * 根据当前 unit 数据树和 staged 文件结果建立装配文档索引。
 *
 * @param input 当前数据树、staged 文件和调用方覆盖文档。
 * @returns 可供本轮 direct child 对比使用的装配文档索引。
 */
function createAssemblyDocumentsByNodeKeyFromStagedFiles(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
}): ReadonlyMap<string, AssemblyDocument> {
  const assemblyDocumentsByNodeKey = new Map<string, AssemblyDocument>(
    input.assemblyDocumentOverridesByNodeKey ?? [],
  )

  for (const node of collectModelAssetNodesPreorder(input.root)) {
    if (!node.assemblyManifest || assemblyDocumentsByNodeKey.has(node.nodeKey)) {
      continue
    }

    const readResult = findProjectNodeFileReadResult({
      nodeKey: node.nodeKey,
      file: node.assemblyManifest,
      role: 'assembly-manifest',
      fileReadResults: input.fileReadResults,
    })

    if (!readResult) {
      throw new Error(`assembly document staged bytes missing: nodeKey=${node.nodeKey}`)
    }

    assemblyDocumentsByNodeKey.set(
      node.nodeKey,
      parseStagedAssemblyDocumentBytes(node, readResult.bytes),
    )
  }

  return assemblyDocumentsByNodeKey
}

/**
 * 收集当前 staged 区已经拥有字节的文件身份。
 *
 * @param fileReadResults 已 staged 的文件读取结果。
 * @returns staged 文件身份 key 集合。
 */
function createStagedFileIdentityKeySet(
  fileReadResults: readonly ProjectNodeFileReadResult[],
): Set<string> {
  return new Set(fileReadResults.map((result) => buildProjectAssetFileIdentityKey(result.file)))
}

/**
 * 从只读缓存中补入当前计划可直接消费的文件字节。
 *
 * 设计说明：`has(file)` 与 `keys()` 只能说明外层认为某文件进入过缓存，不能证明字节仍可读取。
 * 因此这里必须以 `read(file) !== null` 作为可跳过下载、可加入预读 reader 的唯一依据。
 *
 * @param input 计划节点、现有 staged 文件和外层缓存。
 * @returns 可读缓存文件身份以及需要追加到 staged 区的读取结果。
 */
function collectReadableCachedProjectModelFiles(input: {
  readonly nodes: readonly ModelAssetNode[]
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly fileCache: ProjectModelFileByteCacheAdapter
}): ReadableCachedProjectModelFiles {
  const stagedIdentityKeys = createStagedFileIdentityKeySet(input.fileReadResults)
  const readableCachedIdentityKeys = new Set<string>()
  const readableCachedFileReadResults: ProjectNodeFileReadResult[] = []

  for (const task of collectProjectNodeFileReadTasks(input.nodes)) {
    if (!input.fileCache.has(task.file)) {
      continue
    }

    const cachedBytes = input.fileCache.read(task.file)

    if (cachedBytes === null) {
      continue
    }

    const identityKey = buildProjectAssetFileIdentityKey(task.file)

    readableCachedIdentityKeys.add(identityKey)

    if (stagedIdentityKeys.has(identityKey)) {
      continue
    }

    stagedIdentityKeys.add(identityKey)
    readableCachedFileReadResults.push({
      ...task,
      bytes: new Uint8Array(cachedBytes),
    })
  }

  return {
    identityKeys: readableCachedIdentityKeys,
    fileReadResults: readableCachedFileReadResults,
  }
}

/**
 * 使用 staged 文件字节执行 cad-core 项目模型加载。
 *
 * @param input 执行所需的 root、计划和 staged 文件。
 * @returns cad-core 项目模型加载结果。
 */
async function executeWithStagedProjectModelFiles(input: {
  readonly root: ModelAssetNode
  readonly plan: ProjectModelLoadPlan
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly sceneBindings?: readonly ProjectModelSceneBinding[]
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  readonly signal?: AbortSignal
}): Promise<ProjectModelLoadResult> {
  return executeProjectModelLoad({
    root: input.root,
    plan: input.plan,
    reader: createPreloadedProjectModelReader(input.fileReadResults),
    sceneBindings: input.sceneBindings,
    assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
    signal: input.signal,
  })
}

/**
 * 补齐当前 root 可由已下载容器承载的内部 entry 文件结果。
 *
 * @param root 当前加载单元 root。
 * @param fileReadResults 已 staged 的文件读取结果。
 * @param fileCache 外层只读文件字节缓存适配器。
 * @returns 补齐容器 entry 后的 staged 文件读取结果。
 */
async function completeEmbeddedProjectModelFiles(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly fileCache: ProjectModelFileByteCacheAdapter
}): Promise<readonly ProjectNodeFileReadResult[]> {
  const readableCachedFiles = collectReadableCachedProjectModelFiles({
    nodes: collectModelAssetNodesPreorder(input.root),
    fileReadResults: input.fileReadResults,
    fileCache: input.fileCache,
  })

  return completeProjectModelFileReadResultsFromEmbeddedContainers(
    input.root,
    input.fileReadResults,
    readableCachedFiles.identityKeys,
  )
}

/**
 * 在一次 expand 内按节点与 package 文件身份解析并缓存最终项目模型源文档。
 *
 * 原生 v5/v6 project-model 直接复用；v4 只在首次命中时执行一次 legacy migration。Promise
 * 本身进入缓存，因此 null 结果和解析异常也不会在后续 materialize/coverage pass 中重复计算。
 *
 * @param input 节点、package 文件、staged 字节、装配工作文档与事务缓存。
 * @returns 单次解压得到的 assembly 与原生/迁移后的最终 project-model。
 */
function resolveInternalPuppyProjectModelDocuments(input: {
  readonly root: ModelAssetNode
  readonly packageFile: ModelAssetFile
  readonly bytes: Uint8Array
  readonly assemblyDocument?: AssemblyDocument
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
}): Promise<InternalPuppyProjectModelDocuments> {
  const packageReadKey = buildProjectNodeFileReadKey(
    input.root.nodeKey,
    input.packageFile,
    'node-package',
  )
  const cachedPromise = input.projectModelDocumentsPromiseByPackageReadKey.get(packageReadKey)
  if (cachedPromise) {
    return cachedPromise
  }

  const documentsPromise = parsePuppyPackageProjectModelDocuments(input.bytes).then((documents) => ({
    assembly: documents.assembly,
    projectModel: documents.projectModel ?? (input.assemblyDocument
      ? migrateLegacyPuppyProjectModelDocument({
          assembly: documents.assembly,
          assemblyDocument: input.assemblyDocument,
        })
      : null),
  }))
  input.projectModelDocumentsPromiseByPackageReadKey.set(packageReadKey, documentsPromise)
  return documentsPromise
}

/**
 * 按公共后端扩展策略检查并补齐一个装配节点自己的 direct children。
 *
 * 设计说明：这里的 `targetNode` 就是本次检查的 root。命中 `.puppy` 的装配节点不会直接消费
 * CadNode.children，但仍必须读取该节点自己的 `_assembly.json`，并与该节点 `.puppy`
 * 包内一级 hierarchy 对比；只有 `.puppy` 未覆盖且文档有采纳版本时，才通过 GetNodeTree 补齐权威子树。
 *
 * @param input 当前完整加载结果、待检查节点和后端读取上下文。
 * @returns 待检查节点自己的补齐结果。
 */
async function createPuppyAssemblyDirectChildPatchForNode(input: {
  readonly assemblyDocumentsByNodeKey: ReadonlyMap<string, AssemblyDocument>
  readonly targetNode: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly backendBaseUrl: string
  readonly nodeReloadAdapter: NodeReloadApiAdapter
  /** 同一次 expand 跨 pass 传递的 RPC 去重状态。 */
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
  /** 按节点与 package 文件身份共享的最终项目模型源文档缓存。 */
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
  /** 当前 loading unit 已准备的 complete 快照。 */
  readonly completeGlbPackageSnapshotsByNodeKey: ReadonlyMap<
    string,
    PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
  >
}): Promise<{
  readonly result: RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
}> {
  const packageFile = resolvePuppyAssemblyPackageRootFile(input.targetNode)
  const packageReadResult = packageFile
    ? findProjectNodeFileReadResult({
        nodeKey: input.targetNode.nodeKey,
        file: packageFile,
        role: 'node-package',
        fileReadResults: input.fileReadResults,
      })
    : null
  const assemblyDocument = input.assemblyDocumentsByNodeKey.get(input.targetNode.nodeKey)
  const completeSnapshot = input.completeGlbPackageSnapshotsByNodeKey.get(input.targetNode.nodeKey)
  let coverageContext: PuppyAssemblyProjectModelCoverageContext | null = null
  if (packageFile && packageReadResult && assemblyDocument) {
    const documents = await resolveInternalPuppyProjectModelDocuments({
      root: input.targetNode,
      packageFile,
      bytes: packageReadResult.bytes,
      assemblyDocument,
      projectModelDocumentsPromiseByPackageReadKey: input.projectModelDocumentsPromiseByPackageReadKey,
    })
    coverageContext = createPuppyAssemblyProjectModelCoverageContext({
      root: input.targetNode,
      projectModel: documents.projectModel,
    })
  }
  const completeGlbCoverage = completeSnapshot
    ? {
        packageFileId: completeSnapshot.packageFile.fileId,
        rootDirectInstanceIds: completeSnapshot.occurrenceGraph.directChildInstanceIds,
        rootDirectAssemblyNodeIds: new Set(
          [...completeSnapshot.occurrenceGraph.directChildInstanceIds]
            .map(instanceId => completeSnapshot.occurrenceGraph.occurrencesByInstanceId.get(instanceId)?.nodeId)
            .filter((nodeId): nodeId is string => Boolean(nodeId)),
        ),
      }
    : undefined
  return createRootPuppyAssemblyMissingFirstLevelNodeSnapshotForInternalExpandTransaction({
    currentResult: {
      root: input.targetNode,
      designTree: { roots: [] },
      sceneBindings: [],
      runtimeState: {
        rootNodeKey: input.targetNode.nodeKey,
        bindingsByNodeKey: new Map(),
      },
      assemblyDocumentsByNodeKey: input.assemblyDocumentsByNodeKey,
    },
    backendBaseUrl: input.backendBaseUrl,
    nodeReloadAdapter: input.nodeReloadAdapter,
    coverageContext,
    ...(completeGlbCoverage ? { completeGlbCoverage } : {}),
    internalTransactionState: input.internalTransactionState,
  })
}

/**
 * 对当前 load root 中已经存在的装配节点执行一轮 direct child 后端扩展。
 *
 * 设计说明：本轮只遍历进入本轮时已经存在的节点。每个装配节点都以自己作为 load root
 * 执行同一套 direct child diff，避免 OpenProject 和 GetNodeTree 对新增节点判断不一致。
 *
 * @param input 当前加载结果、staged 文件和后端上下文。
 * @returns 本轮 patch 后的完整 load root 与诊断信息。
 */
async function createPuppyAssemblyDirectChildPatchPass(input: {
  readonly root: ModelAssetNode
  readonly assemblyDocumentsByNodeKey: ReadonlyMap<string, AssemblyDocument>
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly backendBaseUrl: string
  readonly nodeReloadAdapter: NodeReloadApiAdapter
  /** 本轮起点的内部 RPC 去重状态快照。 */
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
  /** 整个 loading unit expand 共享的最终项目模型源文档缓存。 */
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
  /** 当前数据树中已经准备完成的 complete 快照。 */
  readonly completeGlbPackageSnapshotsByNodeKey: ReadonlyMap<
    string,
    PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
  >
}): Promise<PuppyAssemblyDirectChildPatchPassResult> {
  const targets = collectModelAssetNodesPreorder(input.root)
  const insertedNodeKeys: string[] = []
  const skippedUnversionedNodeKeys: string[] = []
  const invalidNodeKeys: string[] = []
  let root = input.root
  let internalTransactionState = createInternalPuppyAssemblyExpandTransactionState(
    input.internalTransactionState.insertedNodeKeyByReferenceLocator,
  )

  for (const target of targets) {
    const currentTarget = findModelAssetNodeByKey(root, target.nodeKey)

    if (!currentTarget) {
      continue
    }

    const patchOutput = await createPuppyAssemblyDirectChildPatchForNode({
      assemblyDocumentsByNodeKey: input.assemblyDocumentsByNodeKey,
      targetNode: currentTarget,
      fileReadResults: input.fileReadResults,
      backendBaseUrl: input.backendBaseUrl,
      nodeReloadAdapter: input.nodeReloadAdapter,
      internalTransactionState,
      projectModelDocumentsPromiseByPackageReadKey: input.projectModelDocumentsPromiseByPackageReadKey,
      completeGlbPackageSnapshotsByNodeKey: input.completeGlbPackageSnapshotsByNodeKey,
    })
    const patch = patchOutput.result
    internalTransactionState = patchOutput.internalTransactionState

    appendUniqueNodeKeys(insertedNodeKeys, patch.insertedNodeKeys)
    appendUniqueNodeKeys(skippedUnversionedNodeKeys, patch.skippedUnversionedNodeKeys)
    appendUniqueNodeKeys(invalidNodeKeys, patch.invalidNodeKeys)

    if (patch.insertedNodeKeys.length > 0) {
      root = replaceModelAssetNodeByKey(root, currentTarget.nodeKey, patch.root)
    }
  }

  return {
    root,
    insertedNodeKeys,
    skippedUnversionedNodeKeys,
    invalidNodeKeys,
    internalTransactionState,
  }
}

/**
 * 为当前 loading unit 数据树重建计划并补读缺失文件。
 *
 * 设计说明：一个 loading unit 内部可以经历多轮 GetNodeTree 扩展；这些轮次只准备数据，
 * 不能提前触发 unit 解析。这里因此只返回 root-load 计划和 staged 文件，不调用 cad-core load。
 *
 * @param input 当前数据树、已有 staged 文件和读取上下文。
 * @returns 当前数据树对应的完整读取计划和 staged 文件。
 */
async function prepareProjectModelBackendLoadUnitFiles(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly reader: ModelAssetReader
  readonly fileCache: ProjectModelFileByteCacheAdapter
  readonly signal?: AbortSignal
}): Promise<Pick<ExpandedProjectModelBackendLoadUnit, 'plan' | 'fileReadResults'>> {
  const plan = createProjectModelLoadPlan({
    kind: 'root-load',
    root: input.root,
  })
  const readableCachedFiles = collectReadableCachedProjectModelFiles({
    nodes: plan.nodesToLoad,
    fileReadResults: input.fileReadResults,
    fileCache: input.fileCache,
  })
  const fileReadResultsWithReadableCache = [
    ...input.fileReadResults,
    ...readableCachedFiles.fileReadResults,
  ]
  const missingFileReadTasks = collectMissingProjectModelFileReadTasksFromCache(
    plan.nodesToLoad,
    fileReadResultsWithReadableCache,
    readableCachedFiles.identityKeys,
  )
  const missingFileReadResults = await readProjectModelFileReadTasks(
    missingFileReadTasks,
    input.reader,
    input.signal,
  )
  const fileReadResults = await completeEmbeddedProjectModelFiles({
    root: input.root,
    fileReadResults: [...fileReadResultsWithReadableCache, ...missingFileReadResults],
    fileCache: input.fileCache,
  })

  return {
    plan,
    fileReadResults,
  }
}

/**
 * 从 staged `.puppy` 字节中递归恢复 v5/v6 项目 occurrence 树。
 *
 * 每个版本文件节点只读取自己的唯一装配离线包。包内 occurrence 节点没有 packageFiles，
 * 因此不会被再次当作独立容器解析，也不会重复下载或展开同一份几何。
 *
 * @param input 当前数据树和 staged 文件。
 * @returns 已恢复所有可用 v5/v6 项目文档的不可变模型树。
 */
async function materializePuppyProjectModelTreesFromStagedFiles(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly assemblyDocumentsByNodeKey: ReadonlyMap<string, AssemblyDocument>
  /** 整个 loading unit expand 共享的最终项目模型源文档缓存。 */
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
}): Promise<ModelAssetNode> {
  const visit = async (node: ModelAssetNode): Promise<ModelAssetNode> => {
    const containerFile = resolvePuppyAssemblyPackageRootFile(node)
    let materializedNode = node

    if (containerFile) {
      const packageReadResult = findProjectNodeFileReadResult({
        nodeKey: node.nodeKey,
        file: containerFile,
        role: 'node-package',
        fileReadResults: input.fileReadResults,
      })
      if (!packageReadResult) {
        throw new Error(
          `project model puppy staged bytes missing: nodeKey=${node.nodeKey}, fileId=${containerFile.fileId}`,
        )
      }
      const assemblyDocument = input.assemblyDocumentsByNodeKey.get(node.nodeKey)
      const documents = await resolveInternalPuppyProjectModelDocuments({
        root: node,
        packageFile: containerFile,
        bytes: packageReadResult.bytes,
        assemblyDocument,
        projectModelDocumentsPromiseByPackageReadKey: input.projectModelDocumentsPromiseByPackageReadKey,
      })
      if (documents.projectModel) {
        const materialized = materializePuppyProjectModelTree({
          root: node,
          assembly: documents.assembly,
          projectModel: documents.projectModel,
          containerFile,
        })
        materializedNode = {
          ...materialized,
          children: mergeMaterializedPuppyRootChildrenByProvenance({
            sourceChildren: node.children,
            materializedChildren: materialized.children,
            containerNodeKey: node.nodeKey,
            packageFileId: containerFile.fileId,
            sourceChildBridges: assemblyDocument
              ? createMaterializedPuppyRootSourceChildBridges({
                  document: assemblyDocument,
                  rootNodeKey: node.nodeKey,
                  packageFileId: containerFile.fileId,
                })
              : [],
          }),
        }
      }
    }

    let childrenChanged = false
    const children: ModelAssetNode[] = []
    for (const child of materializedNode.children) {
      const nextChild = await visit(child)
      children.push(nextChild)
      childrenChanged = childrenChanged || nextChild !== child
    }

    return childrenChanged
      ? { ...materializedNode, children }
      : materializedNode
  }

  return visit(input.root)
}

/**
 * 预读当前加载单元文件，并在构建最终计划前消费 v5/v6 项目 occurrence 文档。
 *
 * @param input 当前数据树、已有 staged 文件和读取上下文。
 * @returns 项目树、加载计划和 staged 文件均已同步的结果。
 */
async function prepareProjectModelBackendLoadUnitFilesWithProjectModel(input: {
  readonly root: ModelAssetNode
  readonly fileReadResults: readonly ProjectNodeFileReadResult[]
  readonly reader: ModelAssetReader
  readonly fileCache: ProjectModelFileByteCacheAdapter
  /** 外层 working 装配文档覆盖，确保 v4 migration 与后续 coverage 使用同一文档。 */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /** 整个 loading unit expand 共享的最终项目模型源文档缓存。 */
  readonly projectModelDocumentsPromiseByPackageReadKey: Map<
    string,
    Promise<InternalPuppyProjectModelDocuments>
  >
  readonly signal?: AbortSignal
}): Promise<Pick<ExpandedProjectModelBackendLoadUnit, 'root' | 'plan' | 'fileReadResults'>> {
  const preparedFiles = await prepareProjectModelBackendLoadUnitFiles(input)
  const assemblyDocumentsByNodeKey = createAssemblyDocumentsByNodeKeyFromStagedFiles({
    root: input.root,
    fileReadResults: preparedFiles.fileReadResults,
    assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
  })
  const root = await materializePuppyProjectModelTreesFromStagedFiles({
    root: input.root,
    fileReadResults: preparedFiles.fileReadResults,
    assemblyDocumentsByNodeKey,
    projectModelDocumentsPromiseByPackageReadKey: input.projectModelDocumentsPromiseByPackageReadKey,
  })
  if (root === input.root) {
    return {
      root,
      plan: preparedFiles.plan,
      fileReadResults: preparedFiles.fileReadResults,
    }
  }

  const materializedFiles = await prepareProjectModelBackendLoadUnitFiles({
    ...input,
    root,
    fileReadResults: preparedFiles.fileReadResults,
  })
  return {
    root,
    plan: materializedFiles.plan,
    fileReadResults: materializedFiles.fileReadResults,
  }
}

/**
 * 在 loading unit 内完成所有需要的后端树扩展与文件预读。
 *
 * 设计说明：
 * 1. unit root 是本次业务加载边界；
 * 2. 每轮 direct child 对比时，当前被检查节点才是临时 root；
 * 3. 所有轮次完成后才允许外层执行一次 cad-core load，避免 GetNodeTree 每补一层就触发 unit 解析。
 *
 * @param input 后端项目模型加载准备输入。
 * @returns 数据树与 staged 文件都已稳定的 loading unit。
 */
async function expandProjectModelBackendLoadUnitData(
  input: PrepareProjectModelBackendLoadInput,
): Promise<ExpandedProjectModelBackendLoadUnit> {
  let expandContext: InternalProjectModelBackendExpandContext = {
    projectModelDocumentsPromiseByPackageReadKey: new Map(),
    puppyAssemblyTransactionState: createInternalPuppyAssemblyExpandTransactionState(),
    completeGlbPackageSnapshotPromiseByReadKey: new Map(),
  }
  const initialUnit = await prepareProjectModelBackendLoadUnitFilesWithProjectModel({
    root: input.planInput.root,
    fileReadResults: [],
    reader: input.reader,
    fileCache: input.fileCache,
    assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
    projectModelDocumentsPromiseByPackageReadKey: expandContext.projectModelDocumentsPromiseByPackageReadKey,
    signal: input.signal,
  })
  let root = initialUnit.root
  let plan = initialUnit.plan
  let fileReadResults = initialUnit.fileReadResults
  let completeGlbPackageSnapshotsByNodeKey = await createPreparedCompleteGlbPackageSnapshotsByNodeKey({
    root,
    fileReadResults,
    signal: input.signal,
    snapshotPromiseByReadKey: expandContext.completeGlbPackageSnapshotPromiseByReadKey,
  })

  if (!input.nodeReloadAdapter) {
    return {
      root,
      plan,
      fileReadResults,
      rootPuppyPatch: createEmptyRootPuppyPatch(root),
      completeGlbPackageSnapshotsByNodeKey,
    }
  }

  const insertedNodeKeys: string[] = []
  const skippedUnversionedNodeKeys: string[] = []
  const invalidNodeKeys: string[] = []
  for (let passIndex = 0; passIndex < MAX_PUPPY_ASSEMBLY_DIRECT_CHILD_PATCH_PASSES; passIndex += 1) {
    const assemblyDocumentsByNodeKey = createAssemblyDocumentsByNodeKeyFromStagedFiles({
      root,
      fileReadResults,
      assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
    })
    const patchPass = await createPuppyAssemblyDirectChildPatchPass({
      root,
      assemblyDocumentsByNodeKey,
      fileReadResults,
      backendBaseUrl: input.backendBaseUrl,
      nodeReloadAdapter: input.nodeReloadAdapter,
      internalTransactionState: expandContext.puppyAssemblyTransactionState,
      projectModelDocumentsPromiseByPackageReadKey: expandContext.projectModelDocumentsPromiseByPackageReadKey,
      completeGlbPackageSnapshotsByNodeKey,
    })
    expandContext = {
      projectModelDocumentsPromiseByPackageReadKey: expandContext.projectModelDocumentsPromiseByPackageReadKey,
      puppyAssemblyTransactionState: createInternalPuppyAssemblyExpandTransactionState(
        patchPass.internalTransactionState.insertedNodeKeyByReferenceLocator,
      ),
      completeGlbPackageSnapshotPromiseByReadKey: expandContext.completeGlbPackageSnapshotPromiseByReadKey,
    }

    appendUniqueNodeKeys(insertedNodeKeys, patchPass.insertedNodeKeys)
    appendUniqueNodeKeys(skippedUnversionedNodeKeys, patchPass.skippedUnversionedNodeKeys)
    appendUniqueNodeKeys(invalidNodeKeys, patchPass.invalidNodeKeys)

    if (patchPass.insertedNodeKeys.length === 0) {
      return {
        root,
        plan,
        fileReadResults,
        rootPuppyPatch: {
          root,
          insertedNodeKeys,
          skippedUnversionedNodeKeys,
          invalidNodeKeys,
        },
        completeGlbPackageSnapshotsByNodeKey,
      }
    }

    const nextUnit = await prepareProjectModelBackendLoadUnitFilesWithProjectModel({
      root: patchPass.root,
      fileReadResults,
      reader: input.reader,
      fileCache: input.fileCache,
      assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
      projectModelDocumentsPromiseByPackageReadKey: expandContext.projectModelDocumentsPromiseByPackageReadKey,
      signal: input.signal,
    })

    root = nextUnit.root
    plan = nextUnit.plan
    fileReadResults = nextUnit.fileReadResults
    completeGlbPackageSnapshotsByNodeKey = await createPreparedCompleteGlbPackageSnapshotsByNodeKey({
      root,
      fileReadResults,
      signal: input.signal,
      snapshotPromiseByReadKey: expandContext.completeGlbPackageSnapshotPromiseByReadKey,
    })
  }

  throw new Error(
    `后端树 direct child 扩展超过最大轮数：maxPasses=${MAX_PUPPY_ASSEMBLY_DIRECT_CHILD_PATCH_PASSES}`,
  )
}

/**
 * 执行单个后端项目模型加载单元的统一准备流程。
 *
 * 设计说明：
 * 1. loading unit 内部先完成所有 GetNodeTree 数据树扩展与版本文件预读；
 * 2. 每轮扩展都以当前被检查装配节点作为临时 root，对比该节点自己的 `_assembly.json` 与 `.puppy`；
 * 3. 数据树稳定后才执行一次 cad-core load，避免补一层 GetNodeTree 就触发一次 unit 解析。
 *
 * @param input 后端项目模型加载准备输入。
 * @returns 最终加载结果、最终计划、staged 文件与后端树扩展诊断。
 */
export async function prepareProjectModelBackendLoad(
  input: PrepareProjectModelBackendLoadInput,
): Promise<PreparedProjectModelBackendLoadResult> {
  const expandedUnit = await expandProjectModelBackendLoadUnitData(input)
  const loadResult = await executeWithStagedProjectModelFiles({
    root: expandedUnit.root,
    plan: expandedUnit.plan,
    fileReadResults: expandedUnit.fileReadResults,
    sceneBindings: input.sceneBindings,
    assemblyDocumentOverridesByNodeKey: input.assemblyDocumentOverridesByNodeKey,
    signal: input.signal,
  })

  return {
    loadResult,
    plan: expandedUnit.plan,
    fileReadResults: expandedUnit.fileReadResults,
    completeGlbPackageSnapshotsByNodeKey: expandedUnit.completeGlbPackageSnapshotsByNodeKey,
    rootPuppyPatch: {
      ...expandedUnit.rootPuppyPatch,
      root: loadResult.root,
    },
  }
}
